import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { ThreeDots } from "react-loader-spinner";

import {
  Dialog,
  DialogContent,
  FormControl,
  Select,
  MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import Button from "../../../components/CustomButtons/Button.js";
import ModalHeader from "../../../components/ModalHeader";
import useThemeColors from "../../../hooks/useThemeColors";
import { getAuditLog } from "../../../services/accessService";

const useStyles = makeStyles(() => ({
  filters: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    marginBottom: "12px",
  },
  list: {
    maxHeight: "55vh",
    overflowY: "auto",
  },
  row: {
    display: "flex",
    gap: "12px",
    padding: "8px 4px",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    fontSize: "12.5px",
    alignItems: "baseline",
  },
  ts: {
    color: "#8a909c",
    whiteSpace: "nowrap",
    fontSize: "11px",
    minWidth: "120px",
  },
  action: { fontWeight: 600, whiteSpace: "nowrap" },
  body: { color: "#3C4858", flex: 1 },
  actor: { color: "#8a909c", fontSize: "11px" },
  empty: {
    color: "#888",
    fontStyle: "italic",
    padding: "24px 4px",
    textAlign: "center",
  },
  spinnerWrap: { display: "flex", justifyContent: "center", padding: "30px" },
  loadMore: { textAlign: "center", marginTop: "8px" },
  diff: {
    marginTop: "4px",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  added: { color: "#2e7d32", fontSize: "12px", lineHeight: "1.35" },
  removed: { color: "#c62828", fontSize: "12px", lineHeight: "1.35" },
  diffMark: { fontWeight: 700, marginRight: "4px" },
}));

const ACTION_OPTIONS = [
  "created",
  "preset_applied",
  "permissions_changed",
  "scope_changed",
  "copied_from",
  "expiration_set",
  "expiration_cleared",
  "cleared",
  "expired",
];

const PAGE = 30;

export default function AuditLogDialog({ open, onClose, atomByCode }) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { clientColor } = useThemeColors();

  const labelFor = (code) => {
    const a = atomByCode && atomByCode[code];
    return a ? t(a.label_i18n_key, code) : code;
  };

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [actionFilter, setActionFilter] = useState("");

  const fetchPage = async (reset) => {
    setLoading(true);
    try {
      const nextOffset = reset ? 0 : offset;
      const data = await getAuditLog({
        action: actionFilter || undefined,
        limit: PAGE,
        offset: nextOffset,
      });
      setTotal(data.total || 0);
      setOffset(nextOffset + (data.logs || []).length);
      setRows((prev) =>
        reset ? data.logs || [] : [...prev, ...(data.logs || [])],
      );
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setRows([]);
      setOffset(0);
      fetchPage(true);
    }
    // eslint-disable-next-line
  }, [open, actionFilter]);

  const actionLabel = (a) => t(`access_management.audit.actions.${a}`, a);

  // Contexto curto (preset aplicado, origem da copia, escopo).
  const headline = (r) => {
    const c = r.changes || {};
    switch (r.action) {
      case "preset_applied":
        return `${t("access_management.audit.preset")}: ${c.preset || "—"}`;
      case "scope_changed":
        return t("access_management.audit.scope_updated");
      case "copied_from":
        return `${t("access_management.audit.from")}: ${c.source_email || "—"}`;
      default:
        return "";
    }
  };

  // Diff detalhado: nomes das permissoes concedidas (+) e removidas (-).
  const renderDiff = (r) => {
    const c = r.changes || {};
    const added = c.added || [];
    const removed = c.removed || [];
    if (!added.length && !removed.length) return null;
    return (
      <div className={classes.diff}>
        {added.length > 0 && (
          <div className={classes.added}>
            <span className={classes.diffMark}>+</span>
            {added.map(labelFor).join(", ")}
          </div>
        )}
        {removed.length > 0 && (
          <div className={classes.removed}>
            <span className={classes.diffMark}>−</span>
            {removed.map(labelFor).join(", ")}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <ModalHeader
        title={t("access_management.audit.title")}
        onClose={onClose}
      />
      <DialogContent>
        <div className={classes.filters}>
          <FormControl>
            <Select
              value={actionFilter}
              displayEmpty
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <MenuItem value="">
                {t("access_management.audit.all_actions")}
              </MenuItem>
              {ACTION_OPTIONS.map((a) => (
                <MenuItem key={a} value={a}>
                  {actionLabel(a)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <span className={classes.actor}>
            {t("access_management.audit.total", { count: total })}
          </span>
        </div>

        {loading && rows.length === 0 ? (
          <div className={classes.spinnerWrap}>
            <ThreeDots color={clientColor} height={40} width={40} />
          </div>
        ) : rows.length === 0 ? (
          <div className={classes.empty}>
            {t("access_management.audit.empty")}
          </div>
        ) : (
          <div className={classes.list}>
            {rows.map((r, idx) => (
              <div key={idx} className={classes.row}>
                <span className={classes.ts}>
                  {moment
                    .utc(r.timestamp)
                    .local()
                    .locale(i18n.language)
                    .format("DD/MM/YY HH:mm")}
                </span>
                <span className={classes.action}>{actionLabel(r.action)}</span>
                <span className={classes.body}>
                  {r.target || "—"}
                  {headline(r) ? ` · ${headline(r)}` : ""}
                  {renderDiff(r)}
                  <div className={classes.actor}>
                    {t("access_management.audit.by")} {r.actor || "system"}
                  </div>
                </span>
              </div>
            ))}
            {rows.length < total && (
              <div className={classes.loadMore}>
                <Button
                  simple
                  color="client"
                  size="sm"
                  onClick={() => fetchPage(false)}
                  disabled={loading}
                >
                  {t("access_management.audit.load_more")}
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

AuditLogDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  atomByCode: PropTypes.object,
};

AuditLogDialog.defaultProps = {
  atomByCode: {},
};
